import {
    Component,
    HostListener,
    Inject,
    Optional,
    QueryList,
    ViewChildren,
    ViewEncapsulation,
} from '@angular/core';
import {
    MAT_DIALOG_DATA,
    MatDialogRef
} from '@angular/material/dialog';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { DialogButton } from '@shared/components/dialogs/confirm-dialog/dialog-button';
import { ThemePalette } from '@angular/material/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import {
    ConfirmDialogFormControlBase,
    ConfirmDialogFormSelectControl
} from '@sharedComponents/dialogs/confirm-dialog/confirm-dialog-form-control';
import { AppNotificationService } from '@core/services/app-notification.service';
import { Observable, of } from 'rxjs';
import { debounceTime, map, startWith, switchMap } from 'rxjs/operators';
import { DEFAULT_DEBOUNCE_TIME } from '@modules/bcm/@shared/constants';
import { isString } from '@shared/functions/is-string';
import { Berth } from '@shared/models/berth';
import { SafeHtml } from '@angular/platform-browser';
import { TolgeeService } from '@core/translation/tolgee.service';

@Component({
    selector: 'u2b-confirm-dialog',
    templateUrl: './confirm-dialog.component.html',
    styleUrls: ['./confirm-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class U2bConfirmDialogComponent {

    @ViewChildren(MatAutocompleteTrigger) autocompleteTriggers: QueryList<MatAutocompleteTrigger>;

    public formGroup: UntypedFormGroup;

    public confirmDialogFormControls: ConfirmDialogFormControlBase[];

    public confirmTitle: string;

    public confirmTitleTheme: ThemePalette;

    public confirmBody: string | SafeHtml;

    public yesButton: DialogButton = {
        color: 'accent',
        text: TolgeeService.translate('common.yes')
    };

    public noButton: DialogButton = {
        color: null,
        text: TolgeeService.translate('common.no')
    };

    selectLists?: { [key: string]: Observable<any[]> };

    filteredSelectLists: { [key: string]: Observable<any[]> };

    private _focusYesButtonOnOpen = false;

    constructor(public dialogRef: MatDialogRef<U2bConfirmDialogComponent>,
                @Optional() @Inject(MAT_DIALOG_DATA) data: any,
                private _appNotificationService: AppNotificationService,
                private _tolgee: TolgeeService) {
        dialogRef.addPanelClass('confirm-dialog');
        this._focusYesButtonOnOpen = !!data?.focusYesButtonOnOpen;

        if (data?.confirmDialogFormControls?.length) {
            this.formGroup = new UntypedFormGroup({});
            this.confirmDialogFormControls = [...data.confirmDialogFormControls];
            this.confirmDialogFormControls.forEach(formControl => {
                if (formControl.type === 'select') {
                    const {name, defaultValue, validators, list, listAttribute} = (formControl as ConfirmDialogFormSelectControl<any>);
                    const newControl = new UntypedFormControl(defaultValue, validators);
                    this.formGroup.addControl(name, newControl);

                    if (!this.selectLists) {
                        this.selectLists = {};
                        this.filteredSelectLists = {};
                    }

                    this.selectLists[name] = of(list);
                    this.filteredSelectLists[name] = newControl.valueChanges
                        .pipe(
                            startWith(this.selectLists[name]),
                            debounceTime(DEFAULT_DEBOUNCE_TIME),
                            switchMap((value) => isString(value)
                                ? this._filterList(this.selectLists[name], value, listAttribute)
                                : this.selectLists[name])
                        );

                } else {
                    const {name, defaultValue, validators} = formControl;
                    this.formGroup.addControl(name, new UntypedFormControl(defaultValue, validators));
                }
            });
        }
    }

    public displayListItemWith(controlCfg: ConfirmDialogFormControlBase): (_: any) => string {
        return (item: any): string => {
            if (!item) {
                return '';
            }
            const {listAttribute, listAttributeTranslation} = controlCfg as ConfirmDialogFormSelectControl<any>;
            const rawValue = item[listAttribute];
            // Mirror the option template (`item[listAttributeTranslation] | translate : null : item[listAttribute]`)
            // so the selected value shows the translated label instead of the raw key.
            return listAttributeTranslation
                ? this._tolgee.translate(item[listAttributeTranslation], null, rawValue)
                : rawValue;
        };
    }

    @HostListener('keydown.enter', ['$event'])
    onEnterKey(event: Event): void {
        // Buttons aktivieren sich bei Enter selbst. Ein preventDefault() hier würde diesen Klick
        // abbrechen und aus „Nein"/Schließen ein „Ja" machen.
        if ((event.target as HTMLElement)?.closest?.('button')) {
            return;
        }

        // Enter gehört einem offenen Overlay (Autocomplete-Auswahl übernehmen), nicht dem Dialog.
        if (event.defaultPrevented || this.autocompleteTriggers?.some(trigger => trigger.panelOpen)) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        this.yesButtonClick();
    }

    yesButtonClick(): void {
        if (this.formGroup && !this.formGroup.valid) {
            this.formGroup.markAllAsTouched();
            this._appNotificationService.showError(this._tolgee.translate('common.checkFieldsHighlight'));
            return;
        }
        this.dialogRef.close(this.formGroup?.value || true);
    }

    private _filterList(list$: Observable<any[]>,
                        filter: string = '',
                        attribute: string): Observable<Berth[]> {
        return list$.pipe(
            map((list: any[]) => list.filter(item => item[attribute].toLowerCase().includes(filter.toLowerCase())))
        );
    }
}
